import {FC, useEffect, useState} from 'react'
import {Link, useHistory} from 'react-router-dom'
import {useWebSocketContext} from '../../../../app/context/WebSocketContext'
import {useUserNotifications} from '../../../../app/hooks/notifications/useNotifications'
import {readNotification} from '../../../../app/services/notificationService'

interface NotificationItem {
  id: number
  verb: string
  description?: string
  timestamp?: string
  isRead: boolean
  type?: string
  target_url?: string | null
  targetUrl?: string | null
}

interface HeaderNotificationsMenuProps {
  onNotificationRead?: () => void
  open: boolean
}

// ===== Seguridad =====
// Misma allowlist que /notifications page: solo rutas internas relativas.
// Evita XSS via `javascript:` y open-redirect a dominios externos si
// target_url viniera manipulado.
const INTERNAL_PATH_REGEX = /^\/(?!\/)[A-Za-z0-9/_\-?=&.%:]*$/
const isSafeInternalPath = (value: unknown): value is string => {
  if (typeof value !== 'string') return false
  if (value.length === 0 || value.length > 512) return false
  if (/\s/.test(value)) return false
  return INTERNAL_PATH_REGEX.test(value)
}

const HeaderNotificationsMenu: FC<HeaderNotificationsMenuProps> = ({onNotificationRead, open}) => {
  const {subscribe, isConnected} = useWebSocketContext()
  const {data} = useUserNotifications()
  const navigate = useHistory()
  const [notifications, setNotifications] = useState<NotificationItem[]>([])

  useEffect(() => {
    if (data && Array.isArray(data)) {
      setNotifications(data)
    }
  }, [data])

  const pendingRequests = notifications.filter((n) => !n.isRead).length

  useEffect(() => {
    if (!isConnected) {
      return
    }

    const unsubscribe = subscribe('notification', (data) => {
      const newNotification: NotificationItem = {
        id: data.id || Date.now(),
        verb: data.verb || 'New notification',
        description: data.description || data.message || '',
        timestamp: data.timestamp || 'Now',
        isRead: false,
        type: data.type,
        target_url: data.target_url ?? data.targetUrl ?? null,
      }

      setNotifications((prev) => [newNotification, ...prev])
    })

    return () => {
      unsubscribe()
    }
  }, [subscribe, isConnected])

  const handleItemClick = async (notification: NotificationItem) => {
    // Marca como read localmente (optimista).
    if (!notification.isRead) {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? {...n, isRead: true} : n))
      )
      if (onNotificationRead) {
        onNotificationRead()
      }
      // Fire-and-forget: si falla el backend, el read se recuperara en el
      // proximo fetch. No bloqueamos la navegacion por esto.
      readNotification(notification.id).catch(() => {
        /* logueado por interceptor global; UX no depende del ack */
      })
    }

    // Navegacion deshabilitada a peticion: no redirige a target_url
    /* const raw = notification.target_url ?? notification.targetUrl
    if (isSafeInternalPath(raw)) {
      navigate.push(raw)
    } */
  }

  return (
    <div className={`menu menu-sub menu-sub-dropdown notifications-menu ${open ? 'show' : ''}`}>
      {/* REQUESTS SECTION */}
      <div className='notifications-menu__section'>
        <div className='notifications-menu__title'>REQUESTS</div>

        <div className='notifications-menu__card'>
          <div className='notifications-menu__badge'>
            <div className='notifications-menu__badge-number'>{pendingRequests}</div>
          </div>
          <div className='notifications-menu__card-text'>Pending Experiences Requests</div>
        </div>
      </div>

      {/* NOTIFICATIONS LIST */}
      <div className='notifications-menu__section'>
        <div className='notifications-menu__list'>
          {notifications.length === 0 ? (
            <div className='notifications-menu__empty'>There are no notifications</div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`notifications-menu__item ${
                  !notification.isRead ? 'notifications-menu__item--unread' : ''
                }`}
                style={{cursor: 'default'}}
              >
                <div className='notifications-menu__item-title'>{notification.verb}</div>
                {notification.description && (
                  <div className='notifications-menu__item-message'>{notification.description}</div>
                )}
                {notification.timestamp && (
                  <div className='notifications-menu__item-time'>{notification.timestamp}</div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* VIEW ALL LINK - Deshabilitado */}
      <div className='notifications-menu__view-all'>
        <span style={{cursor: 'default', opacity: 0.6}}>
          view all notifications
        </span>
      </div>
    </div>
  )
}

export {HeaderNotificationsMenu}
